import {
  LeaveDuration,
  Meridiem,
  type LeaveDayCountParams,
} from '@gokrew/models/leaves';

/** The longest span the server counts (two years); longer is not asked. */
const MAX_SPAN_DAYS = 731;

/** The date fields both request forms share. */
export interface LeaveDayCountFormValues {
  leaveDuration?: LeaveDuration;
  leaveDate?: string | Date;
  startDate?: string | Date;
  endDate?: string | Date;
  meridiem?: Meridiem;
}

/** A form date as 'YYYY-MM-DD', converted as the request payloads do. */
function isoDay(value?: string | Date): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value.slice(0, 10) || undefined;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  return undefined;
}

/**
 * What a request form asks the server's day-count endpoint while the leave
 * is typed, or undefined while the dates are incomplete or invalid. An
 * hourly leave asks for its whole day: 0 days back means the date is a
 * weekly off day or a holiday, so it cannot be booked.
 */
export function leaveDayCountParams(
  values: LeaveDayCountFormValues
): LeaveDayCountParams | undefined {
  switch (values.leaveDuration) {
    case LeaveDuration.MULTI_DAY: {
      const startDate = isoDay(values.startDate);
      const endDate = isoDay(values.endDate);
      if (!startDate || !endDate || endDate < startDate) return undefined;
      const span =
        (Date.parse(`${endDate}T00:00:00Z`) -
          Date.parse(`${startDate}T00:00:00Z`)) /
          86_400_000 +
        1;
      if (!(span <= MAX_SPAN_DAYS)) return undefined;
      return {
        startDate,
        endDate,
        startSession: Meridiem.AM,
        endSession: Meridiem.PM,
      };
    }
    case LeaveDuration.HALF_DAY: {
      const day = isoDay(values.leaveDate);
      if (!day || !values.meridiem) return undefined;
      return {
        startDate: day,
        endDate: day,
        startSession: values.meridiem,
        endSession: values.meridiem,
      };
    }
    case LeaveDuration.FULL_DAY:
    case LeaveDuration.HOURLY: {
      const day = isoDay(values.leaveDate);
      if (!day) return undefined;
      return {
        startDate: day,
        endDate: day,
        startSession: Meridiem.AM,
        endSession: Meridiem.PM,
      };
    }
    default:
      return undefined;
  }
}
